/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SecurityModule
 */

import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';

import {
    IAviCollectionDataGridConfig,
    IAviCollectionDataGridRow,
} from 'ng/modules/data-grid/components/avi-collection-data-grid/avi-collection-data-grid.types';

import { GeoDbCollection }
    from 'ajs/modules/geo-db/factories/geo-db.collection.factory';

import * as globalL10n from 'global-l10n';
import * as l10n from './geo-db-list.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type TGeoDbCollection = typeof GeoDbCollection;

/**
 * @description Geo Db list page component.
 *
 * @author Hitesh Mandav.
 */
@Component({
    selector: 'geo-db-list',
    templateUrl: './geo-db-list.component.html',
})
export class GeoDbListComponent implements OnInit, OnDestroy {
    /**
     * Get keys from global source bundles for template usage.
     */
    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Configuration object to display Geo Db collection.
     */
    public geoDbGridConfig: IAviCollectionDataGridConfig;

    /**
     * Keys from source bundles for template usage.
     */
    private readonly l10nKeys = l10nKeys;

    /**
     * Collection object to store the Geo Db collection.
     */
    private readonly geoDbCollection: GeoDbCollection;

    constructor(
        private readonly l10nService: L10nService,
        @Inject(GeoDbCollection)
        GeoDbCollection: TGeoDbCollection,
    ) {
        l10nService.registerSourceBundles(dictionary);

        this.geoDbCollection = new GeoDbCollection();
    }

    /** @override */
    public ngOnInit(): void {
        const { objectName } = this.geoDbCollection;

        this.geoDbGridConfig = {
            id: `${objectName}-list-page`,
            collection: this.geoDbCollection,
            defaultSorting: 'name',
            fields: [
                {
                    id: 'name',
                    label: this.l10nService.getMessage(globalL10nKeys.nameLabel),
                    transform: (row: IAviCollectionDataGridRow) => row.getName(),
                },
                {
                    id: 'is-federated',
                    label: this.l10nService.getMessage(globalL10nKeys.federatedLabel),
                    transform: (row: IAviCollectionDataGridRow) => {
                        return row.getConfig().is_federated ?
                            this.l10nService.getMessage(globalL10nKeys.yesValueLabel) :
                            this.l10nService.getMessage(globalL10nKeys.noValueLabel);
                    },
                },
                {
                    id: 'files-count',
                    label: this.l10nService.getMessage(l10nKeys.columnTitleFiles),
                    transform: (row: IAviCollectionDataGridRow) =>
                        row.getConfig().files.getConfig().length,
                },
                {
                    id: 'mapping-count',
                    label: this.l10nService.getMessage(l10nKeys.columnTitleMappings),
                    transform: (row: IAviCollectionDataGridRow) =>
                        row.getConfig().mappings.getConfig().length,
                },
            ],
            layout: {
                placeholderMessage: this.l10nService.getMessage(globalL10nKeys.noItemsFoundLabel),
            },
        };
    }

    /** @override */
    public ngOnDestroy(): void {
        this.geoDbCollection.destroy();
    }
}
